import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { AuthProvider, useAuth } from '@/providers/AuthProvider'
import { I18nProvider, ThemeProvider, ToastProvider } from '@/providers/UiProviders'
import { AppShell } from '@/shell/AppShell'
import { LockScreen, LoginScreen, OnboardingScreen, SessionExpiredScreen } from '@/screens/auth/AuthScreens'
import { isOnboarded, markOnboarded } from '@/lib/auth'
import { DashboardScreen } from '@/screens/DashboardScreen'
import { UsersScreen } from '@/screens/users/UsersScreen'
import { UserCard } from '@/screens/users/UserCard'
import { UserSessions } from '@/screens/users/UserSessions'
import { UserGroups } from '@/screens/users/UserGroups'
import { UserOwnedGroups } from '@/screens/users/UserOwnedGroups'
import { UserBots } from '@/screens/users/UserBots'
import { GroupsScreen } from '@/screens/groups/GroupsScreen'
import { GroupCard } from '@/screens/groups/GroupCard'
import { GroupMembers } from '@/screens/groups/GroupMembers'
import { GroupAdmins } from '@/screens/groups/GroupAdmins'
import { GroupBots } from '@/screens/groups/GroupBots'
import { BotsScreen } from '@/screens/bots/BotsScreen'
import { BotCard } from '@/screens/bots/BotCard'
import { GiftsScreen } from '@/screens/gifts/GiftsScreen'
import { GromAuktionScreen } from '@/screens/gromauktion/GromAuktionScreen'
import { ReportsScreen } from '@/screens/reports/ReportsScreen'
import { VerificationRequestsScreen } from '@/screens/verification/VerificationRequestsScreen'
import { SupportScreen } from '@/screens/support/SupportScreen'
import { AppealsScreen } from '@/screens/support/AppealsScreen'
import { EmptyState } from '@/components/States'
import { useT } from '@/i18n'

function NotFound() {
  const t = useT()
  return <EmptyState title={t.states.notFoundTitle} />
}

/** A1 — визначає, що показати залежно від стану сесії. */
function AuthGate() {
  const { state, identity, signOut } = useAuth()

  // A1.08 — onboarding: один раз при першому вході (на кожен username).
  // `null` = ще невідомо (щоб не блимнуло до того, як ефект прочитає стан).
  const [onboarded, setOnboarded] = useState<boolean | null>(null)
  useEffect(() => {
    if (state === 'authenticated' && identity) setOnboarded(isOnboarded(identity.username))
    else if (state !== 'authenticated') setOnboarded(null)
  }, [state, identity])

  if (state === 'loading') return <div className="min-h-screen bg-surface-0" />
  if (state === 'anonymous') return <LoginScreen />
  if (state === 'expired') return <SessionExpiredScreen onContinue={signOut} />
  if (state === 'locked') return <LockScreen />

  if (identity && onboarded === false) {
    return (
      <OnboardingScreen
        username={identity.username}
        role={identity.role}
        onDone={() => {
          markOnboarded(identity.username)
          setOnboarded(true)
        }}
      />
    )
  }

  return (
    <Routes>
      <Route
        path="*"
        element={
          <AppShell>
            <Routes>
              <Route path="/" element={<DashboardScreen />} />
              <Route path="/users" element={<UsersScreen />} />
              <Route path="/users/:id/sessions" element={<UserSessions />} />
              <Route path="/users/:id/groups" element={<UserGroups />} />
              <Route path="/users/:id/owned-groups" element={<UserOwnedGroups />} />
              <Route path="/users/:id/bots" element={<UserBots />} />
              <Route path="/users/:id" element={<UserCard />} />
              <Route path="/groups" element={<GroupsScreen />} />
              <Route path="/groups/:id/members" element={<GroupMembers />} />
              <Route path="/groups/:id/admins" element={<GroupAdmins />} />
              <Route path="/groups/:id/bots" element={<GroupBots />} />
              <Route path="/groups/:id" element={<GroupCard />} />
              <Route path="/bots" element={<BotsScreen />} />
              <Route path="/bots/:id" element={<BotCard />} />
              <Route path="/gifts" element={<GiftsScreen />} />
              <Route path="/gromauktion" element={<GromAuktionScreen />} />
              <Route path="/reports" element={<ReportsScreen />} />
              <Route path="/verification-requests" element={<VerificationRequestsScreen />} />
              <Route path="/appeals" element={<AppealsScreen />} />
              <Route path="/support" element={<SupportScreen />} />
              <Route path="/index.html" element={<Navigate to="/" replace />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </AppShell>
        }
      />
    </Routes>
  )
}

export function App() {
  return (
    <BrowserRouter>
      <I18nProvider>
        <ThemeProvider>
          <ToastProvider>
            <AuthProvider>
              <AuthGate />
            </AuthProvider>
          </ToastProvider>
        </ThemeProvider>
      </I18nProvider>
    </BrowserRouter>
  )
}
